import type { Nullable } from "@core/domain/types/nullable.type";
import { LoadLayoutStore } from "@core/presentacion/component/feedback/load-layout/load-layout.store";
import { ToastManagerStore } from "@core/presentacion/component/feedback/toast-manager/toast-manager.store";
import { BaseViewModel } from "@core/presentacion/view-model/base/base.viewmodel";
import { EmployeeTypeEnum } from "@entity/domain/models/employee/employee-type.model";
import { GetContractedHoursForPeriodUseCase } from "@entity/domain/usecases/employee/get-contracted-hours-for-period.usecase";
import { ProjectFinancialEntityDataSource } from "@project/data/datasource/project-financial-entity.datasource";
import { ProjectEmployeeDedicationMapper } from "@project/data/mappers/project-employee-dedication.mapper";
import type { FinancialEntity } from "@project/domain/models/financial-entity/financial-entities.model";
import { ProjectEmployee } from "@project/domain/models/project-employees.model";
import { ProjectFinancialEntity } from "@project/domain/models/project-financial-entities.model";
import { GetAllFinancialEntitiesUseCase } from "@project/domain/usecases/financial-entity/get-all-financial-entities.usecase";
import { CreateProjectEmployeeDedicationUseCase } from "@project/domain/usecases/project-employee-dedication/create-project-employee-dedication.usecase";
import { DedicationFormValuesValidated } from "@project/presentation/components/project-form/employees/add-dedication-modal-form.view";
import { inject, injectable } from "inversify";
import { DateTime } from "luxon";
import { action, makeObservable, observable, runInAction } from "mobx";

@injectable()
export class AddDedicationModalFormViewModel extends BaseViewModel {
    @observable
    financialEntities: ProjectFinancialEntity[] = [];

    @observable
    financialEntityNameMap: Record<number, string> = {};

    @observable
    contractedHoursForPeriod: Nullable<number> = null;

    constructor(
        @inject(CreateProjectEmployeeDedicationUseCase)
        private readonly createProjectEmployeeDedicationUseCase: CreateProjectEmployeeDedicationUseCase,
        @inject(ProjectEmployeeDedicationMapper)
        private readonly projectEmployeeDedicationMapper: ProjectEmployeeDedicationMapper,
        @inject(ProjectFinancialEntityDataSource)
        private readonly projectFinancialEntityDataSource: ProjectFinancialEntityDataSource,
        @inject(GetAllFinancialEntitiesUseCase)
        private readonly getAllFinancialEntitiesUseCase: GetAllFinancialEntitiesUseCase,
        @inject(GetContractedHoursForPeriodUseCase)
        private readonly getContractedHoursForPeriodUseCase: GetContractedHoursForPeriodUseCase,
    ) {
        super();
        makeObservable(this);
    }

    @action
    async fetchFinancialEntities(projectId: number): Promise<void> {
        try {
            // Fetch project financial entities
            const result =
                await this.projectFinancialEntityDataSource.fetchAllBy({
                    projectId,
                });
            let projectFinancialEntities: ProjectFinancialEntity[] = [];

            if (result.isRight()) {
                const entitiesObj = result.getOrThrow();
                projectFinancialEntities = entitiesObj.projectFinancialEntities;
            }

            // Fetch all financial entities (for names)
            const allFinancialEntities: FinancialEntity[] =
                await this.getAllFinancialEntitiesUseCase.execute();

            // Build id->name map
            const nameMap: Record<number, string> = {};
            allFinancialEntities.forEach((fe) => {
                nameMap[fe.id] = fe.name;
            });

            runInAction(() => {
                this.financialEntities = projectFinancialEntities;
                this.financialEntityNameMap = nameMap;
            });
        } catch (error) {
            console.error("Error fetching financial entities:", error);
            runInAction(() => {
                this.financialEntities = [];
                this.financialEntityNameMap = {};
            });
        }
    }

    @action
    setContractedHoursForPeriod(hours: Nullable<number>): void {
        this.contractedHoursForPeriod = hours;
    }

    async fetchContractedHoursForPeriod(
        employeeId: number,
        startDate: string,
        endDate: string,
    ): Promise<void> {
        const hours = await this.getContractedHoursForPeriodUseCase.execute(
            employeeId,
            startDate,
            endDate,
        );
        this.setContractedHoursForPeriod(hours);
    }

    async addDedication(
        projectEmployeeId: number,
        dedication: DedicationFormValuesValidated,
    ): Promise<boolean> {
        LoadLayoutStore.start();

        const projectEmployeeDedication =
            this.projectEmployeeDedicationMapper.mapFromFormToCreate(
                projectEmployeeId,
                dedication,
                dedication.projectFinantialEntityId,
            );
        const created =
            await this.createProjectEmployeeDedicationUseCase.execute(
                projectEmployeeDedication,
            );

        if (created) {
            ToastManagerStore.success();
        }

        LoadLayoutStore.finish();

        return !!created;
    }

    getRemainingEmployeeHours(
        projectEmployee: ProjectEmployee,
        startDate: DateTime<boolean>,
        endDate: DateTime<boolean>,
    ): number {
        // Para freelancers, usar las horas contratadas base
        if (projectEmployee.employee.type === EmployeeTypeEnum.Freelance) {
            if (projectEmployee.dedications.length === 0) {
                return projectEmployee.employee.contractedHours ?? 0;
            }

            const dedicatedHours = projectEmployee.dedications.reduce(
                (totalHoursDedicated, currentDedication) =>
                    totalHoursDedicated + currentDedication.hoursDedicated,
                0,
            );

            return projectEmployee.totalAvailableHours - dedicatedHours;
        }

        // Para empleados y voluntarios, calcular horas restantes disponibles
        const contractedHours =
            this.contractedHoursForPeriod ??
            projectEmployee.employee.contractedHours ??
            0;

        // Filtrar dedicaciones de ESTE proyecto que se solapan con el período especificado
        // IMPORTANTE: projectEmployee.dedications solo contiene las dedicaciones de este ProjectEmployee (este proyecto)
        const overlappingDedications = projectEmployee.dedications.filter(
            (dedication) => {
                // Verificar si hay solapamiento entre los rangos de fechas
                // Hay solapamiento si: dedication.start <= period.end Y dedication.end >= period.start
                return (
                    dedication.startDate <= endDate &&
                    dedication.endDate >= startDate
                );
            },
        );

        // Calcular horas ya dedicadas en este proyecto en el período especificado
        const dedicatedHours = overlappingDedications.reduce(
            (totalHoursDedicated, currentDedication) =>
                totalHoursDedicated + currentDedication.hoursDedicated,
            0,
        );

        // Retornar las horas disponibles (contratadas - ya dedicadas en este proyecto)
        return Math.max(0, contractedHours - dedicatedHours);
    }
}
